import { useMemo, useCallback } from 'react';
import { useSession } from '@/features/guided-financial-planning/context';
import { usePlanComparisonScenarios } from './use-plan-comparison-scenarios';
import { useFundingSources } from './use-funding-sources';
import { getPremiumAmount } from './use-quotes-display';

/**
 * Validation error returned when finalization checks fail
 */
export interface FinalizeValidationError {
  /** Unique error code for programmatic handling */
  code: string;
  /** Human-readable error message */
  message: string;
  /** Optional field or section the error relates to */
  field?: string;
}

/**
 * Result of running finalize validation
 */
export interface FinalizeValidationResult {
  isValid: boolean;
  errors: FinalizeValidationError[];
}

/**
 * Validation rule function type.
 * Returns an error if validation fails, or null if it passes.
 */
type ValidationRule = () => FinalizeValidationError | null;

/**
 * Hook to validate session state before finalization.
 *
 * Returns validation state and a validate function that can be called
 * before showing the confirmation dialog or finalizing the session.
 *
 * Usage:
 * ```tsx
 * const { isValid, errors, validate } = useFinalizeValidation();
 *
 * const handleNextClick = () => {
 *   const result = validate();
 *   if (!result.isValid) {
 *     // Show errors to user
 *     return;
 *   }
 *   // Proceed with finalization
 * };
 * ```
 */
export function useFinalizeValidation() {
  const { session } = useSession();
  const planComparison = usePlanComparisonScenarios();
  const fundingSources = useFundingSources();

  const selectedQuotes = (session?.stepData?.selectedQuotes ?? {}) as Record<
    string,
    SelectedQuoteData
  >;
  const policyFundingAllocations = (session?.stepData?.policyFundingAllocations ?? {}) as Record<
    string,
    Record<string, number>
  >;
  const selfFundingSources = fundingSources?.selfFunding ?? {};

  /**
   * Define validation rules.
   * Each rule returns an error if validation fails, or null if it passes.
   * Add new validation rules here as needed.
   */
  const validationRules: ValidationRule[] = useMemo(() => {
    return [
      // Rule: Must have at least one selected quote
      () => {
        const hasSelectedQuote = Object.keys(selectedQuotes).length > 0;
        if (!hasSelectedQuote) {
          return {
            code: 'NO_QUOTE_SELECTED',
            message: 'Please select a policy quote before finalizing.',
            field: 'selectedQuotes',
          };
        }
        return null;
      },

      // Rule: Each selected quote must have funding allocations that cover the cost
      () => {
        for (const [quoteId, quoteData] of Object.entries(selectedQuotes)) {
          const allocations = policyFundingAllocations[quoteId] ?? {};
          const totalAllocated = Object.values(allocations).reduce(
            (sum, amount) => sum + (amount || 0),
            0,
          );

          const policyCost = getPremiumAmount(quoteData.quote);

          if (policyCost > 0 && totalAllocated < policyCost) {
            return {
              code: 'INSUFFICIENT_FUNDING',
              message: `Funding allocations ($${totalAllocated.toLocaleString()}) do not cover the policy cost ($${policyCost.toLocaleString()}).`,
              field: 'policyFundingAllocations',
            };
          }
        }
        return null;
      },

      // Rule: Funding allocations must not exceed the actual assets value on each funding source
      () => {
        for (const [, allocationsForQuote] of Object.entries(policyFundingAllocations)) {
          for (const [selfFundingSourceId, allocatedAmount] of Object.entries(
            allocationsForQuote,
          )) {
            const source = selfFundingSources[selfFundingSourceId];
            if (!source) {
              return {
                code: 'INVALID_FUNDING_SOURCE',
                message: `Account not found. Previously selected account has been removed.`,
                field: 'policyFundingAllocations',
              };
            }

            const availableAssets = source.existingAssetsValue ?? 0;
            if (allocatedAmount > availableAssets) {
              const accountName = source.selfFundingName || source.accountType || 'Account';
              return {
                code: 'ALLOCATION_EXCEEDS_ASSETS',
                message: `Allocation of $${allocatedAmount.toLocaleString()} from "${accountName}" exceeds available assets ($${availableAssets.toLocaleString()}).`,
                field: 'policyFundingAllocations',
              };
            }
          }
        }
        return null;
      },
    ];
  }, [
    selectedQuotes,
    policyFundingAllocations,
    selfFundingSources,
    session?.status,
    planComparison.projectedLtcCost,
  ]);

  /**
   * Run all validation rules and collect errors
   */
  const validate = useCallback((): FinalizeValidationResult => {
    const errors: FinalizeValidationError[] = [];

    for (const rule of validationRules) {
      const error = rule();
      if (error) {
        errors.push(error);
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }, [validationRules]);

  /**
   * Current validation state (computed on each render).
   * Use this for UI state like disabling buttons.
   * Use validate() for imperative validation before actions.
   */
  const validationState = useMemo(() => validate(), [validate]);

  return {
    /** Whether the current state passes all validation rules */
    isValid: validationState.isValid,
    /** Current validation errors */
    errors: validationState.errors,
    /** Function to run validation imperatively */
    validate,
    /** Check if a specific error code is present */
    hasError: useCallback(
      (code: string) => validationState.errors.some(e => e.code === code),
      [validationState.errors],
    ),
    /** Get error by code */
    getError: useCallback(
      (code: string) => validationState.errors.find(e => e.code === code),
      [validationState.errors],
    ),
  };
}
